import { useCallback, useState } from 'react';
import { SINGLE_SELECT_FILTERS } from '../constants/filterConfig';

export type FilterPanelApi = ReturnType<typeof useFilterPanel>;

export const useFilterPanel = () => {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({});
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({});
  }, []);

  const handleAccordionClick = (filterId: string) => {
    setOpenAccordionId((prev) => (prev === filterId ? null : filterId));
  };

  const handleFilterSelect = (filterId: string, optionId: string) => {
    const isSingleSelect = SINGLE_SELECT_FILTERS.includes(filterId);
    const currentSet = selectedFilters[filterId] || new Set();

    let filterSet: Set<string>;

    if (isSingleSelect) {
      // Single-select: clicking the same option deselects it, otherwise replace selection
      if (currentSet.has(optionId)) {
        const { [filterId]: _, ...rest } = selectedFilters;
        setSelectedFilters(rest);

        return;
      } else {
        filterSet = new Set([optionId]);
      }
    } else {
      // Multi-select: toggle the option
      filterSet = new Set(currentSet);
      if (filterSet.has(optionId)) {
        filterSet.delete(optionId);

        if (filterSet.size === 0) {
          const { [filterId]: _, ...rest } = selectedFilters;
          setSelectedFilters(rest);

          return;
        }
      } else {
        filterSet.add(optionId);
      }
    }

    setSelectedFilters({ ...selectedFilters, [filterId]: filterSet });
  };

  const isFilterSelected = (filterId: string, optionId: string) => {
    return selectedFilters[filterId]?.has(optionId) || false;
  };

  const hasSelectedFilters = (filterId: string) => {
    return (selectedFilters[filterId]?.size || 0) > 0;
  };

  const resetAccordion = useCallback(() => {
    setOpenAccordionId(null);
  }, []);

  return {
    selectedFilters,
    openAccordionId,
    handleClearFilters,
    handleAccordionClick,
    handleFilterSelect,
    isFilterSelected,
    hasSelectedFilters,
    resetAccordion,
  };
};
